import {FormGroup, Validators} from '@angular/forms';

import {RiskActionsEnum, RiskClassModel} from '@spa/api';

import {InArrayValidator} from '@spa/validators';

import {SelectControl} from '../../../../shared/classes';
import {ReportTableData, ReportTableDataRow, SelectOptionInterface} from '../../../../shared/components';

import {PepManagerApprovalEnum} from '../../enums';
import {PepDetailsControlsReducedObjectInterface} from '../../interfaces';

import {
    PepDetailsControls,
    PepDetailsDataTableItems,
    PepDetailsStatusSelector,
    PepDetailsApprovalSelector,
    PepDetailsRiskScoreSelector,
    PepDetailsRiskClassificationSelector,
} from '../../../../../assets/descriptors/panel-clients';

export class PepDetailsTrait {
    private constructor() {
    }

    static createDataSource(
        shownControl: boolean,
        controls: SelectControl[],
        riskClassList: RiskClassModel[]
    ): ReportTableDataRow[] {
        const reducedControls = controls
            .reduce((reducedObject: PepDetailsControlsReducedObjectInterface, control: SelectControl) => {
                switch (control.name) {
                    case PepDetailsStatusSelector:
                        reducedObject.status = control;
                        break;
                    case PepDetailsApprovalSelector:
                        reducedObject.approval = control;
                        break;
                    case PepDetailsRiskClassificationSelector:
                        reducedObject.classification = control;
                }

                return reducedObject;
            }, {status: null, approval: null, classification: null});

        reducedControls.classification.options.slice(0, reducedControls.classification.options.length);

        let classifications = [];

        switch (reducedControls.approval.value) {
            case PepManagerApprovalEnum.No:
                classifications = riskClassList
                    .filter((classification: RiskClassModel) => {
                        return classification.riskAction === RiskActionsEnum.Refuse;
                    });

                reducedControls.classification.value = classifications[0] && classifications[0].upperBound;
                break;
            case PepManagerApprovalEnum.Yes:
                classifications = riskClassList
                    .filter((classification: RiskClassModel) => {
                        return classification.riskAction !== RiskActionsEnum.Refuse;
                    });
                break;
        }

        reducedControls.classification.options = classifications.map((classification: RiskClassModel) => {
            return {label: classification.name, value: classification.upperBound};
        });

        const row = new ReportTableDataRow({rowId: null, dataItems: [], disabledActions: {}});

        row.dataItems = PepDetailsTrait.createDataRowItems(reducedControls, shownControl, classifications);

        return [row];
    }

    static createDataRowItems(
        reducedControls: PepDetailsControlsReducedObjectInterface,
        shownControl: boolean,
        filteredClassifications: RiskClassModel[]
    ): ReportTableData[] {
        return PepDetailsDataTableItems().map((tableDataItem: ReportTableData) => {
            switch (tableDataItem.colId) {
                case PepDetailsStatusSelector:
                    const filteredStatusOptions = PepDetailsControls()[0].options
                        .filter((option: SelectOptionInterface) => {
                            return reducedControls.status.value === option.value;
                        });

                    tableDataItem.value = filteredStatusOptions[0] && filteredStatusOptions[0].label;
                    tableDataItem.control = reducedControls.status;

                    tableDataItem.shownControl = shownControl;
                    break;
                case PepDetailsApprovalSelector:
                    let filteredApprovalOptions = [{value: null, label: ''}];

                    if (reducedControls.status.value) {
                        filteredApprovalOptions = PepDetailsControls()[1].options
                            .filter((option: SelectOptionInterface) => {
                                return reducedControls.approval.value === option.value;
                            });
                    }

                    tableDataItem.value = filteredApprovalOptions[0].label;
                    tableDataItem.control = reducedControls.approval;

                    tableDataItem.shownControl = reducedControls.status.value && shownControl;
                    break;
                case PepDetailsRiskClassificationSelector:
                    tableDataItem.value = PepDetailsTrait.getRiskClassificationName(
                        reducedControls,
                        filteredClassifications
                    );

                    tableDataItem.control = reducedControls.classification;

                    tableDataItem.shownControl = reducedControls.status.value &&
                        (reducedControls.approval.value === PepManagerApprovalEnum.Yes) && shownControl;
                    break;
                case PepDetailsRiskScoreSelector:
                    const classValue = reducedControls.classification.value;

                    tableDataItem.value = (!reducedControls.status.value) ||
                    (reducedControls.approval.value === PepManagerApprovalEnum.None) ? '0' : classValue;
                    break;
            }

            return tableDataItem;
        });
    }

    static getRiskClassificationName(
        reducedControls: PepDetailsControlsReducedObjectInterface,
        filteredClassifications: RiskClassModel[]
    ): string {
        if (reducedControls.status.value) {
            if (!reducedControls.approval) {
                return filteredClassifications[0].name;
            }

            const selectedClassification = filteredClassifications
                .filter((classification: RiskClassModel) => {
                    return classification.upperBound === reducedControls.classification.value;
                });

            return selectedClassification[0] && selectedClassification[0].name;
        }

        return '';
    }

    static setRiskClassificationFormControl(
        control: SelectControl,
        formGroup: FormGroup,
        data: { [key: string]: number },
        riskClassList: RiskClassModel[]
    ): void {
        formGroup.get(PepDetailsRiskClassificationSelector).clearValidators();

        if (!data[PepDetailsStatusSelector]) {
            control.value = null;
            formGroup.get(PepDetailsRiskClassificationSelector).setValue(null, {emitEvent: false});
        } else {
            let riskScores = [];

            switch (data[PepDetailsApprovalSelector]) {
                case PepManagerApprovalEnum.No:
                    riskScores = riskClassList.filter((classification: RiskClassModel) => {
                        return classification.riskAction === RiskActionsEnum.Refuse;
                    }).map((classification: RiskClassModel) => classification.upperBound);

                    control.value = riskScores[0];
                    break;
                case PepManagerApprovalEnum.Yes:
                    riskScores = riskClassList.filter((classification: RiskClassModel) => {
                        return classification.riskAction !== RiskActionsEnum.Refuse;
                    }).map((classification: RiskClassModel) => classification.upperBound);

                    control.value = (riskScores.indexOf(data[PepDetailsRiskClassificationSelector]) >= 0) ?
                        data[PepDetailsRiskClassificationSelector] : null;
                    break;
                case PepManagerApprovalEnum.None:
                    control.value = null;
                    formGroup.get(PepDetailsRiskClassificationSelector).setValue(null, {emitEvent: false});
                    break;
            }

            formGroup.get(PepDetailsRiskClassificationSelector).setValue(control.value, {emitEvent: false});

            if (data[PepDetailsApprovalSelector] !== PepManagerApprovalEnum.None) {
                formGroup.get(PepDetailsRiskClassificationSelector)
                    .setValidators([Validators.required, InArrayValidator(riskScores)]);
            }
        }
    }
}
